import React from 'react';
import PropTypes from 'prop-types';
import NextLink from 'next/link.js';
import nextRoutes from 'next-routes';
import { useIntl } from 'react-intl';
import messages from './lib/messages.mjs';
import getRelativePrefix from './lib/getRelativePrefix.mjs';
import isLite from './lib/isLite.mjs';
import isDashboard from './lib/isDashboard.mjs';
import getPrefix from './lib/getPrefix.mjs';

const maybePrefix = '/:prefix(fr)?';
const keywordsParameter = String.raw`:keywords((?!in\/).+?)`;
const locationSearchParameter = ':locationSearch(.+?)';
const englishCareersPagePath = '/fashion-jobs/work-at/:companySlug-jobs/careers/:profilePage(about|news|working-at)';
const germanCareersPagePath = '/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/careers/:profilePage(about|news|working-at)';
const spanishCareersPagePath = '/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/careers/:profilePage(about|news|working-at)';
const frenchCareersPageMultilingualPath = '/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:profilePage(about|news|working-at)';
const frenchCareersPagePath = '/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:profilePage(about|news|working-at)';
const russianCareersPagePath = '/rabota-moda/work-at/:companySlug-jobs/careers/:profilePage(about|news|working-at)';
const hungarianCareersPagePath = '/allasok-a-divatszakmaban/ceg/:companySlug-allasok/careers/:profilePage(about|news|working-at)';
const italianCareersPagePath = '/lavorare-nella-moda/lavori-a/:companySlug-lavori/careers/:profilePage(about|news|working-at)';
const dutchCareersPagePath = '/modevacatures/werken-bij/:companySlug-vacatures/careers/:profilePage(about|news|working-at)';
const flemishCareersPagePath = '/fashion-jobs/werken-bij/:companySlug-jobs/careers/:profilePage(about|news|working-at)';
// eslint-disable-next-line @stylistic/max-len
const englishJobsPath = `/fashion-jobs{/work-at/:companySlug-jobs}?{/in/:citySlug}?{/positions/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const englishJobsCareersPath = `/fashion-jobs/work-at/:companySlug-jobs/careers{/in/:citySlug}?{/positions/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const englishJobsCityPath = `/fashion-jobs{/work-at/:companySlug-jobs}?{/in/:citySlug}?{/positions/:positionSlug-jobs}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const englishJobsCareersCityPath = `/fashion-jobs/work-at/:companySlug-jobs/careers{/in/:citySlug}?{/positions/:positionSlug-jobs}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
const englishJobPath = String.raw`/fashion-jobs/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const englishCareersJobPath = String.raw`/fashion-jobs/work-at/:companySlug-jobs/careers/:slug?-:id(\d+)`;
const englishJobApplyPath = String.raw`/fashion-jobs/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const englishCareersJobApplyPath = String.raw`/fashion-jobs/work-at/:companySlug-jobs/careers/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const germanJobsPath = `/jobs-in-der-mode{/arbeiten-bei/:companySlug-stellenangebote}?{/in/:citySlug}?{/stellen/:positionSlug-stellenangebote}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const germanJobsCareersPath = `/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/careers{/in/:citySlug}?{/stellen/:positionSlug-stellenangebote}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const germanJobsCityPath = `/jobs-in-der-mode{/arbeiten-bei/:companySlug-stellenangebote}?{/in/:citySlug}?{/stellen/:positionSlug-stellenangebote}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const germanJobsCareersCityPath = `/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/careers{/in/:citySlug}?{/stellen/:positionSlug-stellenangebote}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
const germanJobPath = String.raw`/jobs-in-der-mode/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const germanCareersJobPath = String.raw`/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/careers/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const germanJobApplyPath = String.raw`/jobs-in-der-mode/:slug?-:id(\d+)/bewerben`;
// eslint-disable-next-line @stylistic/max-len
const germanCareersJobApplyPath = String.raw`/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/careers/:slug?-:id(\d+)/bewerben`;
// eslint-disable-next-line @stylistic/max-len
const spanishJobsPath = `/ofertas-trabajo-moda{/trabaja-en/:companySlug-trabajos}?{/en/:citySlug}?{/puestos/:positionSlug-trabajos}?{/buscar/${keywordsParameter}}?{/en/${locationSearchParameter}}?/:categoryParam(diseno-y-creativo|pasantias|otro|product-and-supply-chain|gestion-y-venta-en-tiendas|ventas-y-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const spanishJobsCareersPath = `/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/careers{/en/:citySlug}?{/puestos/:positionSlug-trabajos}?{/buscar/${keywordsParameter}}?{/en/${locationSearchParameter}}?/:categoryParam(diseno-y-creativo|pasantias|otro|product-and-supply-chain|gestion-y-venta-en-tiendas|ventas-y-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const spanishJobsCityPath = `/ofertas-trabajo-moda{/trabaja-en/:companySlug-trabajos}?{/en/:citySlug}?{/puestos/:positionSlug-trabajos}?{/buscar/en/${locationSearchParameter}}?/:categoryParam(diseno-y-creativo|pasantias|otro|product-and-supply-chain|gestion-y-venta-en-tiendas|ventas-y-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const spanishJobsCareersCityPath = `/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/careers{/en/:citySlug}?{/puestos/:positionSlug-trabajos}?{/buscar/en/${locationSearchParameter}}?/:categoryParam(diseno-y-creativo|pasantias|otro|product-and-supply-chain|gestion-y-venta-en-tiendas|ventas-y-marketing)?`;
const spanishJobPath = String.raw`/ofertas-trabajo-moda/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const spanishCareersJobPath = String.raw`/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/careers/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const spanishJobApplyPath = String.raw`/ofertas-trabajo-moda/:slug?-:id(\d+)/solicitar`;
// eslint-disable-next-line @stylistic/max-len
const spanishCareersJobApplyPath = String.raw`/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/careers/:slug?-:id(\d+)/solicitar`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsMultilingualPath = `/:prefix(fr)/fashion-jobs{/travailler-chez/:companySlug-recrutement}?{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/${keywordsParameter}}?{/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsMultilingualCityPath = `/:prefix(fr)/fashion-jobs{/travailler-chez/:companySlug-recrutement}?{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsCareersMultilingualPath = `/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/careers{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/${keywordsParameter}}?{/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsCareersMultilingualCityPath = `/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/careers{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobMultilingualPath = String.raw`/:prefix(fr)/fashion-jobs/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const frenchCareersJobMultilingualPath = String.raw`/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:slug?-:id(\d+)`;
const frenchJobApplyMultilingualPath = `/:prefix(fr)/fashion-jobs${
  String.raw`/:slug?-:id(\d+)/postuler`}`;
// eslint-disable-next-line @stylistic/max-len
const frenchCareersJobApplyMultilingualPath = String.raw`/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:slug?-:id(\d+)/postuler`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsPath = `/fashion-jobs{/travailler-chez/:companySlug-recrutement}?{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/${keywordsParameter}}?{/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsCareersPath = `/fashion-jobs/travailler-chez/:companySlug-recrutement/careers{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/${keywordsParameter}}?{/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsCityPath = `/fashion-jobs{/travailler-chez/:companySlug-recrutement}?{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchJobsCareersCityPath = `/fashion-jobs/travailler-chez/:companySlug-recrutement/careers{/a/:citySlug}?{/fonctions/:positionSlug-emplois}?{/search/a/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const frenchCareersJobPath = String.raw`/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:slug?-:id(\d+)`;
const frenchJobApplyPath = String.raw`/fashion-jobs/:slug?-:id(\d+)/postuler`;
// eslint-disable-next-line @stylistic/max-len
const frenchCareersJobApplyPath = String.raw`/fashion-jobs/travailler-chez/:companySlug-recrutement/careers/:slug?-:id(\d+)/postuler`;
// eslint-disable-next-line @stylistic/max-len
const russianJobsPath = `/rabota-moda{/work-at/:companySlug-jobs}?{/in/:citySlug}?{/dolznosti/:positionSlug-vakansii}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const russianJobsCareersPath = `/rabota-moda/work-at/:companySlug-jobs/careers{/in/:citySlug}?{/dolznosti/:positionSlug-vakansii}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const russianJobsCityPath = `/rabota-moda{/work-at/:companySlug-jobs}?{/in/:citySlug}?{/dolznosti/:positionSlug-vakansii}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const russianJobsCareersCityPath = `/rabota-moda/work-at/:companySlug-jobs/careers{/in/:citySlug}?{/dolznosti/:positionSlug-vakansii}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
const russianJobPath = String.raw`/rabota-moda/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const russianCareersJobPath = String.raw`/rabota-moda/work-at/:companySlug-jobs/careers/:slug?-:id(\d+)`;
const russianJobApplyPath = String.raw`/rabota-moda/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const russianCareersJobApplyPath = String.raw`/rabota-moda/work-at/:companySlug-jobs/careers/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const hungarianJobsPath = `/allasok-a-divatszakmaban{/ceg/:companySlug-allasok}?{/varosok/:citySlug}?{/positions/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/varosok/${locationSearchParameter}}?/:categoryParam(dizajn-es-kreativ|gyakornok|egyeb|termek-es-ellatasi-lanc|kiskereskedelem|sales-es-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const hungarianJobsCareersPath = `/allasok-a-divatszakmaban/ceg/:companySlug-allasok/careers{/varosok/:citySlug}?{/positions/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/varosok/${locationSearchParameter}}?/:categoryParam(dizajn-es-kreativ|gyakornok|egyeb|termek-es-ellatasi-lanc|kiskereskedelem|sales-es-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const hungarianJobsCityPath = `/allasok-a-divatszakmaban{/ceg/:companySlug-allasok}?{/varosok/:citySlug}?{/positions/:positionSlug-jobs}?{/search/varosok/${locationSearchParameter}}?/:categoryParam(dizajn-es-kreativ|gyakornok|egyeb|termek-es-ellatasi-lanc|kiskereskedelem|sales-es-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const hungarianJobsCareersCityPath = `/allasok-a-divatszakmaban/ceg/:companySlug-allasok/careers{/varosok/:citySlug}?{/positions/:positionSlug-jobs}?{/search/varosok/${locationSearchParameter}}?/:categoryParam(dizajn-es-kreativ|gyakornok|egyeb|termek-es-ellatasi-lanc|kiskereskedelem|sales-es-marketing)?`;
const hungarianJobPath = String.raw`/allasok-a-divatszakmaban/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const hungarianCareersJobPath = String.raw`/allasok-a-divatszakmaban/ceg/:companySlug-allasok/careers/:slug?-:id(\d+)`;
const hungarianJobApplyPath = `/allasok-a-divatszakmaban${
  String.raw`/:slug?-:id(\d+)/jelentkezes`}`;
// eslint-disable-next-line @stylistic/max-len
const hungarianCareersJobApplyPath = String.raw`/allasok-a-divatszakmaban/ceg/:companySlug-allasok/careers/:slug?-:id(\d+)/jelentkezes`;
// eslint-disable-next-line @stylistic/max-len
const italianJobsPath = `/lavorare-nella-moda{/lavori-a/:companySlug-lavori}?{/in/:citySlug}?{/posizioni/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const italianJobsCareersPath = `/lavorare-nella-moda/lavori-a/:companySlug-lavori/careers{/in/:citySlug}?{/posizioni/:positionSlug-jobs}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const italianJobsCityPath = `/lavorare-nella-moda{/lavori-a/:companySlug-lavori}?{/in/:citySlug}?{/posizioni/:positionSlug-jobs}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const italianJobsCareersCityPath = `/lavorare-nella-moda/lavori-a/:companySlug-lavori/careers{/in/:citySlug}?{/posizioni/:positionSlug-jobs}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|internships|other|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
const italianJobPath = String.raw`/lavorare-nella-moda/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const italianCareersJobPath = String.raw`/lavorare-nella-moda/lavori-a/:companySlug-lavori/careers/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const italianJobApplyPath = String.raw`/lavorare-nella-moda/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const italianCareersJobApplyPath = String.raw`/lavorare-nella-moda/lavori-a/:companySlug-lavori/careers/:slug?-:id(\d+)/apply`;
// eslint-disable-next-line @stylistic/max-len
const dutchJobsPath = `/modevacatures{/werken-bij/:companySlug-vacatures}?{/in/:citySlug}?{/functies/:positionSlug-banen}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const dutchJobsCareersPath = `/modevacatures/werken-bij/:companySlug-vacatures/careers{/in/:citySlug}?{/functies/:positionSlug-banen}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const dutchJobsCityPath = `/modevacatures{/werken-bij/:companySlug-vacatures}?{/in/:citySlug}?{/functies/:positionSlug-banen}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const dutchJobsCareersCityPath = `/modevacatures/werken-bij/:companySlug-vacatures/careers{/in/:citySlug}?{/functies/:positionSlug-banen}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
const dutchJobPath = String.raw`/modevacatures/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const dutchCareersJobPath = String.raw`/modevacatures/werken-bij/:companySlug-vacatures/careers/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const dutchJobApplyPath = String.raw`/modevacatures/:slug?-:id(\d+)/solliciteer`;
// eslint-disable-next-line @stylistic/max-len
const dutchCareersJobApplyPath = String.raw`/modevacatures/werken-bij/:companySlug-vacatures/careers/:slug?-:id(\d+)/solliciteer`;
// eslint-disable-next-line @stylistic/max-len
const flemishJobsPath = `/fashion-jobs{/werken-bij/:companySlug-jobs}?{/in/:citySlug}?{/functies/:positionSlug-vacatures}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const flemishJobsCareersPath = `/fashion-jobs/werken-bij/:companySlug-jobs/careers{/in/:citySlug}?{/functies/:positionSlug-vacatures}?{/search/${keywordsParameter}}?{/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const flemishJobsCityPath = `/fashion-jobs{/werken-bij/:companySlug-jobs}?{/in/:citySlug}?{/functies/:positionSlug-vacatures}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const flemishJobsCareersCityPath = `/fashion-jobs/werken-bij/:companySlug-jobs/careers{/in/:citySlug}?{/functies/:positionSlug-vacatures}?{/search/in/${locationSearchParameter}}?/:categoryParam(design-and-creative|stages|overige|product-and-supply-chain|retail-management-and-in-store|sales-and-marketing)?`;
// eslint-disable-next-line @stylistic/max-len
const flemishCareersJobPath = String.raw`/fashion-jobs/werken-bij/:companySlug-jobs/careers/:slug?-:id(\d+)`;
// eslint-disable-next-line @stylistic/max-len
const flemishJobApplyPath = String.raw`/fashion-jobs/:slug?-:id(\d+)/solliciteer`;
// eslint-disable-next-line @stylistic/max-len
const flemishCareersJobApplyPath = String.raw`/fashion-jobs/werken-bij/:companySlug-jobs/careers/:slug?-:id(\d+)/solliciteer`;
const englishJobsPositionsPath = '/fashion-jobs/positions';
const germanJobsPositionsPath = '/jobs-in-der-mode/stellen';
const spanishJobsPositionsPath = '/ofertas-trabajo-moda/puestos';
const frenchJobsPositionsMultilingualPath = '/:prefix(fr)/fashion-jobs/fonctions';
const frenchJobsPositionsPath = '/fashion-jobs/fonctions';
const russianJobsPositionsPath = '/rabota-moda/dolznosti';
const hungarianJobsPositionsPath = '/allasok-a-divatszakmaban/positions';
const italianJobsPositionsPath = '/lavorare-nella-moda/posizioni';
const dutchJobsPositionsPath = '/modevacatures/functies';
const flemishJobsPositionsPath = '/fashion-jobs/functies';
const englishJobsCitiesPath = '/fashion-jobs/in';
const germanJobsCitiesPath = '/jobs-in-der-mode/in';
const spanishJobsCitiesPath = '/ofertas-trabajo-moda/en';
const frenchJobsCitiesMultilingualPath = '/:prefix(fr)/fashion-jobs/a';
const frenchJobsCitiesPath = '/fashion-jobs/a';
const russianJobsCitiesPath = '/rabota-moda/in';
const hungarianJobsCitiesPath = '/allasok-a-divatszakmaban/varosok';
const italianJobsCitiesPath = '/lavorare-nella-moda/in';
const dutchJobsCitiesPath = '/modevacatures/in';
const flemishJobsCitiesPath = '/fashion-jobs/in';
const englishCompanyProfilesPath = '/fashion-jobs/work-at';
const germanCompanyProfilesPath = '/jobs-in-der-mode/arbeiten-bei';
const spanishCompanyProfilesPath = '/ofertas-trabajo-moda/trabaja-en';
const frenchCompanyProfilesMultilingualPath = '/:prefix(fr)/fashion-jobs/Travailler-chez';
const frenchCompanyProfilesPath = '/fashion-jobs/Travailler-chez';
const russianCompanyProfilesPath = '/rabota-moda/work-at';
const hungarianCompanyProfilesPath = '/allasok-a-divatszakmaban/work-at';
const italianCompanyProfilesPath = '/lavorare-nella-moda/lavori-a';
const dutchCompanyProfilesPath = '/modevacatures/werken-bij';
const flemishCompanyProfilesPath = '/fashion-jobs/werken-bij';
const englishJobsLandingPath = '/fashionjobs';
const ukJobsLandingPath = '/career-centre';
const germanJobsLandingPath = '/stellenmarkt-der-modebranche';
const spanishJobsLandingPath = '/bolsa-de-trabajo-en-moda';
const frenchJobsLandingPath = '/fashion-jobs-offres-d-emploi-mode';
const frenchJobsLandingMultilingualPath = '/:prefix(fr)/fashion-job-recrutement';
const italianJobsLandingPath = '/centro-carriera';
const flemishJobsLandingPath = '/mode-jobs';
const dutchJobsLandingPath = '/mode-vacatures';
const russianJobsLandingPath = '/rabota-v-industrii-mody';
const englishEditorialStatutePath = '/editorial-statute';
const germanEditorialStatutePath = '/redaktionssatzung';
const frenchEditorialStatutePath = `${maybePrefix}/statut-editorial`;
const italianEditorialStatutePath = '/statuto-editoriale';
const spanishEditorialStatutePath = '/estatuto-de-redaccion';
const dutchEditorialStatutePath = '/redactiestatuut';
const russianEditorialStatutePath = '/ustav-redaktzii';
const englishProfilePagePath = '/fashion-jobs/work-at/:companySlug-jobs/:profilePage(about|news|working-at)';
const germanProfilePagePath = '/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/:profilePage(about|news|working-at)';
const spanishProfilePagePath = '/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/:profilePage(about|news|working-at)';
const frenchProfilePageMultilingualPath = '/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/:profilePage(about|news|working-at)';
const frenchProfilePagePath = '/fashion-jobs/travailler-chez/:companySlug-recrutement/:profilePage(about|news|working-at)';
const russianProfilePagePath = '/rabota-moda/work-at/:companySlug-jobs/:profilePage(about|news|working-at)';
const hungarianProfilePagePath = '/allasok-a-divatszakmaban/ceg/:companySlug-allasok/:profilePage(about|news|working-at)';
const italianProfilePagePath = '/lavorare-nella-moda/lavori-a/:companySlug-lavori/:profilePage(about|news|working-at)';
const dutchProfilePagePath = '/modevacatures/werken-bij/:companySlug-vacatures/:profilePage(about|news|working-at)';
const flemishProfilePagePath = '/fashion-jobs/werken-bij/:companySlug-jobs/:profilePage(about|news|working-at)';
const englishEBPEditPath = '/fashion-jobs/work-at/:companySlug-jobs/edit';
const germanEBPEditPath = '/jobs-in-der-mode/arbeiten-bei/:companySlug-stellenangebote/edit';
const spanishEBPEditPath = '/ofertas-trabajo-moda/trabaja-en/:companySlug-trabajos/edit';
const frenchEBPEditMultilingualPath = '/:prefix(fr)/fashion-jobs/travailler-chez/:companySlug-recrutement/edit';
const frenchEBPEditPath = '/fashion-jobs/travailler-chez/:companySlug-recrutement/edit';
const russianEBPEditPath = '/rabota-moda/work-at/:companySlug-jobs/edit';
const hungarianEBPEditPath = '/allasok-a-divatszakmaban/ceg/:companySlug-allasok/edit';
const italianEBPEditPath = '/lavorare-nella-moda/lavori-a/:companySlug-lavori/edit';
const dutchEBPEditPath = '/modevacatures/werken-bij/:companySlug-vacatures/edit';
const flemishEBPEditPath = '/fashion-jobs/werken-bij/:companySlug-jobs/edit';
const englishNewsFeedPath = '/just-in';
const frenchNewsFeedPath = '/fil-info';
const frenchNewsFeedMultilingualPath = '/:prefix(fr)/fil-info';
const dutchNewsFeedPath = '/net-binnen';
const spanishNewsFeedPath = '/ultima-hora';
const germanNewsFeedPath = '/aktuell';
const italianNewsFeedPath = '/ultima-ora';
const portugueseNewsFeedPath = '/ultimas';
const englishPartnerContentPath = '/partner-content';
const frenchPartnerContentPath = '/contenu-partenaire';
const frenchPartnerContentMultilingualPath = '/:prefix(fr)/contenu-partenaire';
const spanishPartnerContentPath = '/contenido-externo';
const germanPartnerContentPath = '/partnerangebot';
const italianPartnerContentPath = '/pubbliredazionale';
const portuguesePartnerContentPath = '/conteudo-patrocinado';
const englishPressReleasesPath = '/press-releases';
const spanishPressReleasesPath = '/comunicados-de-prensa';
const germanPressReleasesPath = '/pressemitteilungen';
const frenchPressReleasesMultilingualPath = '/:prefix(fr)/press-releases';
const englishSubmitJobPostingsPath = '/company/submit-your-job-postings';
const germanSubmitJobPostingsPath = '/company/geben-sie-ihre-stellenanzeigen-auf';
const frenchSubmitJobPostingsPath = '/company/diffusez-vos-offres-d-emploi';
const spanishSubmitJobPostingsPath = '/company/ponga-su-anuncio';
const categoryPath = ':categoryPath(actualite|actualite/business|actualite/culturelle|actualite/culture|actualite/mode|actualite/people|actualite/podcast|actualite/retail|actualite/salons|actualite/historique|actualite/video|featured-in|nachrichten|nachrichten/business|nachrichten/einzelhandel|nachrichten/kultur|nachrichten/messen|nachrichten/mode|nachrichten/personen|nachrichten/hintergrund|nachrichten/podcast|nachrichten/video|news|news/business|news/cultura|news/culture|news/fairs|news/background|news/fashion|news/fiere|news/moda|news/people|news/persone|news/podcast|news/retail|news/sfondo|news/video|nieuws|nieuws/beurzen|nieuws/business|nieuws/cultuur|nieuws/mensen|nieuws/mode|nieuws/multi|nieuws/podcast|nieuws/retail|nieuws/achtergrond|nieuws/video|noticias|noticias/cultura|noticias/empresas|noticias/ferias|noticias/gente|noticias/moda|noticias/podcast|noticias/retail|noticias/contexto|noticias/video|notícias/histórico|press|press/business|press/celebridades|press/celebrity|press/cultura|press/culture|press/cultuur|press/einzelhandel|press/empresas|press/event|press/eventi|press/eventos|press/events|press/fashion|press/gente|press/investigacion|press/kultur|press/ling-shou-ye|press/mensen|press/ming-ren|press/moda|press/mode|press/people|press/persone|press/personen|press/ren|press/ren-wu|press/research|press/retail|press/ricerca|press/shang-wu|press/shi-shang|press/veranstaltungen|press/wen-hua|press/beeznyes|press/znamyeneetostee|press/eesslyedovaneeya|press/kulstura|press/leechnostee|press/reetyeil|presse|presse/business|presse/culture|presse/culturelle|presse/evenements|presse/mode|presse/people|presse/personnalites|presse/recherche|presse/retail|v1|v1/apparel|v1/columns|v1/design|v1/fashion|v1/fashion-news|v1/leads|v1/moda|v1/mode|v1/mode-nachrichten|v1/show|v1/shows-mode|v1/trends|v1/video|xin-wen|xin-wen/bei-jing|xin-wen/huo-dong|xin-wen/ling-shou-ye|xin-wen/podcast|xin-wen/ren|xin-wen/shang-wu|xin-wen/shi-shang|xin-wen/video|xin-wen/wen-hua|novostee|novostee/podcast|novostee/beeznyes|novostee/veedyeo|novostee/vistavkee|novostee/kulstura|novostee/leechnostee|novostee/moda|novostee/reetyeil|uncategorised|education|education/news|education/schools|executive|executive/management|executive/report|press)';
const englishLocalesWithoutUk = [
  'en-AU', 'en-CA', 'en-GH', 'en-IE', 'en-IN', 'en-NG', 'en-NZ', 'en-SG', 'en-US', 'en-ZA',
];
const ukLocales = ['en-GB'];
const englishLocales = [...englishLocalesWithoutUk, ...ukLocales];
const germanLocales = ['de-AT', 'de-CH', 'de-DE'];
const spanishLocales = ['es-AR', 'es-BO', 'es-CL', 'es-CO', 'es-CR', 'es-DO', 'es-EC', 'es-ES', 'es-GT', 'es-HN', 'es-MX', 'es-PA', 'es-PE', 'es-PR', 'es-PY', 'es-SV', 'es-UY', 'es-VE'];
const frenchOnlyLocales = ['fr-CI', 'fr-FR', 'fr-LU', 'fr-SN'];
const frenchNonMultilingualLocales = [...frenchOnlyLocales, 'fr-CH'];
const frenchMultilingualLocales = ['fr-BE', 'fr-CA'];
const netherlandsLocales = ['nl-NL'];
const flemishLocales = ['nl-BE'];
const dutchLocales = [...netherlandsLocales, ...flemishLocales];
const russianLocales = ['ru-RU'];
const italyLocales = ['it-IT'];
const italianLocales = [...italyLocales, 'it-CH'];
const hungarianLocales = ['hu-HU'];
const swissAlternativeLocales = ['fr-CH', 'it-CH'];
const portugueseLocales = ['pt-BR', 'pt-PT'];
const untranslatedLocales = [
  'ar-AE',
  'ar-DZ',
  'ar-EG',
  'ar-JO',
  'ar-LB',
  'ar-MA',
  'ar-SA',
  'ar-TN',
  'az-AZ',
  'bg-BG',
  'bn-BD',
  'cs-CZ',
  'da-DK',
  'el-CY',
  'el-GR',
  'et-EE',
  'fi-FI',
  'fil-PH',
  'he-IL',
  'hr-HR',
  'id-ID',
  'ja-JP',
  'ka-GE',
  'kk-KZ',
  'km-KH',
  'ko-KR',
  'lt-LT',
  'lv-LV',
  'mg-MG',
  'ms-MY',
  'mt-MT',
  'nb-NO',
  'ne-NP',
  'pl-PL',
  'ro-RO',
  'si-LK',
  'sk-SK',
  'sl-SI',
  'sr-RS',
  'sv-SE',
  'sw-KE',
  'th-TH',
  'tr-TR',
  'uk-UA',
  'ur-PK',
  'uz-UZ',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-TW',
];

const categoryTagPage = ':page(trends|fashion-week-web|education|executive)';

function addPathForLocales(route, path, locales, page = route) {
  return locales
    .reduce((routes, locale) => routes
      .add(`${route}.${locale}`, path, page), this);
}

const defineAddPathForLocales = (routes) => {
  // eslint-disable-next-line no-param-reassign
  routes.addPathForLocales = addPathForLocales;

  return routes;
};

const isLiteInBrowser = typeof window !== 'undefined'
  && isLite(getPrefix(window.location.href));

// I need to include this package in order to fix this bug easier:
// https://github.com/zeit/next.js/issues/5214

const getBaseRoutes = () => defineAddPathForLocales(nextRoutes())
  .add(
    'appShell',
    `${maybePrefix}/app-shell/:locale`,
    'appShell',
  )
  .add(
    'logout',
    `${maybePrefix}/logout`,
    'logout',
  )
  .add(
    'register-buyer',
    `${maybePrefix}/marketplace/buyers`,
    'register-buyer',
  )
  .add(
    'change-password',
    `${maybePrefix}/change-password`,
    'change-password',
  )
  .add(
    'profile',
    `${maybePrefix}/profile`,
    'profile',
  )
  .add(
    'edit-user-profile',
    `${maybePrefix}/profile/edit`,
    'edit-user-profile',
  )
  .addPathForLocales(
    'job',
    englishJobPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
      ...flemishLocales,
      ...frenchNonMultilingualLocales,
    ],
  )
  .addPathForLocales(
    'job',
    germanJobPath,
    germanLocales,
  )
  .addPathForLocales(
    'job',
    spanishJobPath,
    spanishLocales,
  )
  .addPathForLocales(
    'job',
    frenchJobMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'job',
    hungarianJobPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'job',
    italianJobPath,
    italianLocales,
  )
  .addPathForLocales(
    'job',
    dutchJobPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'job',
    russianJobPath,
    russianLocales,
  )
  .addPathForLocales(
    'careers-job',
    englishCareersJobPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'careers-job',
    germanCareersJobPath,
    germanLocales,
  )
  .addPathForLocales(
    'careers-job',
    spanishCareersJobPath,
    spanishLocales,
  )
  .addPathForLocales(
    'careers-job',
    frenchCareersJobPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'careers-job',
    frenchCareersJobMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'careers-job',
    hungarianCareersJobPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'careers-job',
    italianCareersJobPath,
    italianLocales,
  )
  .addPathForLocales(
    'careers-job',
    dutchCareersJobPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'careers-job',
    flemishCareersJobPath,
    flemishLocales,
  )
  .addPathForLocales(
    'careers-job',
    russianCareersJobPath,
    russianLocales,
  )
  .add(
    'article',
    `${maybePrefix}\
/${categoryPath}{/:slug}?/:insertedAt(\\d{8}):id(\\d+)`,
    'article',
  )
  .add(
    'lookbookImageUpdate',
    String.raw`/lookbook/{:slug-}?:id(\d+)/update`,
    'lookbook/update',
  )
  .add(
    'author',
    `${maybePrefix}/authors/:authorSlug`,
    'author',
  )
  .add(
    'login',
    `${maybePrefix}/login`,
    'login',
  )
  .add(
    'register',
    `${maybePrefix}/register`,
    'register',
  )
  .add(
    'reset-password',
    `${maybePrefix}/reset-password`,
    'reset-password',
  )
  .add(
    'set-password',
    `${maybePrefix}/set-password`,
    'set-password',
  )
  .add(
    'company-profile',
    `${maybePrefix}/companies/:slug`,
    'company-profile',
  )
  .add(
    'company-profile-edit',
    `${maybePrefix}/companies/:slug/edit`,
    'company-profile-edit',
  );

export const getDashboardRoutes = () => getBaseRoutes()
  .add(
    'dashboard',
    '/',
    'dashboard',
  )
  .add('news-form', '/news-articles/new', 'news-form')
  .add('news-form-edit', '/news-articles/:newsArticleId/edit', 'news-form-edit')
  .add('news-form-success', '/news-articles/new/success', 'news-form-success')
  .add('profiles-brands', '/profiles-brands', 'profiles-brands')
  .add('access-requests', '/access-requests', 'access-requests')
  .add(
    'dashboardJobs',
    '/jobs',
    'dashboard/jobs',
  )
  .add(
    'job-form',
    '/jobs/new',
    'job-form',
  )
  .add(
    'job-form-admin',
    '/admin/jobs/new',
    'job-form-admin',
  )
  .add(
    'job-form-edit-admin',
    '/admin/jobs/:jobId/edit',
    'job-form-edit-admin',
  )
  .add(
    'job-form-edit',
    '/jobs/:jobId/edit',
    'job-form-edit',
  )
  .add(
    'profile-form',
    '/profiles/new',
    'profile-form',
  )
  .add(
    'profile-form-edit',
    '/profiles/:profileId/edit',
    'profile-form-edit',
  )
  .add(
    'profiles-list',
    '/profiles',
    'profiles-list',
  )
  .add(
    'logo-form',
    '/logos/new',
    'logo-form',
  )
  .add(
    'templates',
    '/templates',
    'dashboard/templates',
  )
  .add(
    'dashboardCategory',
    // eslint-disable-next-line @stylistic/max-len
    String.raw`/lookbook/categories/:slug-:categoryId(\d+){/search/:search(.*)}?`,
    'dashboard/logos/gallery',
  )
  .add(
    'dashboardImage',
    String.raw`/lookbook/{:slug-}?:id(\d+)`,
    'dashboard/logos/image',
  )
  .add(
    'templateForm',
    '/templates/new',
    'dashboard/templates/new',
  )
  .add(
    'templateFormEdit',
    '/templates/:templateId/edit',
    'dashboard/templates/edit',
  )
  .add(
    'dashboard-events',
    '/events',
    'dashboard/events',
  )
  .add(
    'event-form',
    '/events/new',
    'dashboard/events/new',
  )
  .add(
    'event-form-edit',
    '/events/:id/edit',
    'dashboard/events/edit',
  )
  .add(
    'job-versions',
    '/jobs/:jobId/versions',
    'job-versions',
  )
  .add(
    'applicants-list',
    '/candidates/:jobId(.+)?',
    'applicants-list',
  )
  .add(
    'bundles',
    '/bundles',
    'bundles',
  )
  .add(
    'order',
    '/order',
    'order',
  )
  .add(
    'create-newsletter',
    '/newsletter',
    '/dashboard/create-newsletter',
  )
  .add(
    'retool',
    '/:retoolPath(users|open_ai|published_jobs|special_pages|tags|accounts|brands|category_keywords|lookbook_images|marketplace_kpi|credits|translate|image_generator|used_and_expiring_credits|news|authors|news_articles|expiring_jobs|qr_code_generator|finance|orders|courses|schedules)',
    'retool',
  );

export const getSiteRoutes = () => getBaseRoutes()
  .add(
    'index',
    maybePrefix,
    isLiteInBrowser ? 'lite-home-page' : 'index',
  )
  .add('lookbookCategories', '/lookbook/categories', 'lookbook/categories')
  .add(
    'lookbookCategory',
    // eslint-disable-next-line @stylistic/max-len
    String.raw`/lookbook/categories/:slug-:categoryId(\d+){/search/:search(.*)}?`,
    'lookbook/category',
  )
  .add('lookbook', '/lookbook{/search/:search(.*)}?', 'lookbook')
  .add('lookbookUpload', '/lookbook/upload', 'lookbook/upload')

  .add('lookbookImage', String.raw`/lookbook/{:slug-}?:id(\d+)`, 'lookbook/image')
  .add(
    'case',
    `${maybePrefix}/case/:slug`,
    'case',
  )
  .add(
    'search',
    `${maybePrefix}/:searchPath(search|zoek|such-ergebnis|\
recherche|buscador|ricerca)`,
    'search',
  )
  .add(
    'tags',
    `${maybePrefix}/tags`,
    'tags',
  )
  .addPathForLocales(
    'news-feed',
    frenchNewsFeedMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'news-feed',
    frenchNewsFeedPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'news-feed',
    spanishNewsFeedPath,
    spanishLocales,
  )
  .addPathForLocales(
    'news-feed',
    germanNewsFeedPath,
    germanLocales,
  )
  .addPathForLocales(
    'news-feed',
    dutchNewsFeedPath,
    dutchLocales,
  )
  .addPathForLocales(
    'news-feed',
    italianNewsFeedPath,
    italianLocales,
  )
  .addPathForLocales(
    'news-feed',
    portugueseNewsFeedPath,
    portugueseLocales,
  )
  .addPathForLocales(
    'news-feed',
    englishNewsFeedPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...hungarianLocales,
      ...russianLocales,
    ],
  )
  .add(
    'most-read-daily',
    `${maybePrefix}/most-read-daily`,
    'most-read-daily',
  )
  .addPathForLocales(
    'jobs-landing',
    englishJobsLandingPath,
    [
      ...englishLocalesWithoutUk,
      ...untranslatedLocales,
      ...portugueseLocales,
      ...hungarianLocales,
    ],
  )
  .addPathForLocales(
    'jobs-landing',
    ukJobsLandingPath,
    ukLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    germanJobsLandingPath,
    [
      ...germanLocales,
      ...swissAlternativeLocales,
    ],
  )
  .addPathForLocales(
    'jobs-landing',
    spanishJobsLandingPath,
    spanishLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    frenchJobsLandingMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    frenchJobsLandingPath,
    frenchOnlyLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    italianJobsLandingPath,
    italyLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    flemishJobsLandingPath,
    flemishLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    dutchJobsLandingPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'jobs-landing',
    russianJobsLandingPath,
    russianLocales,
  )
  .add(
    'partners',
    '/:prefix(fr)?/partners',
    'partners',
  )
  .add(
    'tag',
    `${maybePrefix}/tags/:slug(.*)`,
    'tag',
  )
  .add(
    'trends',
    `${maybePrefix}/trends`,
    'trends',
  )
  .addPathForLocales(
    'partner-content',
    frenchPartnerContentMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'partner-content',
    frenchPartnerContentPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'partner-content',
    spanishPartnerContentPath,
    spanishLocales,
  )
  .addPathForLocales(
    'partner-content',
    germanPartnerContentPath,
    germanLocales,
  )
  .addPathForLocales(
    'partner-content',
    italianPartnerContentPath,
    italianLocales,
  )
  .addPathForLocales(
    'partner-content',
    portuguesePartnerContentPath,
    portugueseLocales,
  )
  .addPathForLocales(
    'partner-content',
    englishPartnerContentPath,
    [
      ...englishLocales,
      ...dutchLocales,
      ...untranslatedLocales,
      ...hungarianLocales,
      ...russianLocales,
    ],
  )
  .addPathForLocales(
    'press-releases',
    spanishPressReleasesPath,
    spanishLocales,
  )
  .addPathForLocales(
    'press-releases',
    germanPressReleasesPath,
    germanLocales,
  )
  .addPathForLocales(
    'press-releases',
    frenchPressReleasesMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'press-releases',
    englishPressReleasesPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...hungarianLocales,
      ...russianLocales,
      ...dutchLocales,
      ...italianLocales,
      ...portugueseLocales,
      ...frenchNonMultilingualLocales,
    ],
  )
  .add(
    'fashion-week-web',
    `${maybePrefix}/fashion-week-web`,
    'fashion-week-web',
  )
  .add(
    'education',
    `${maybePrefix}/education`,
    'education',
  )
  .add(
    'executive',
    `${maybePrefix}/executive`,
    'executive',
  )
  .add(
    'company-profile-products',
    `${maybePrefix}/companies/:slug/products`,
    'company-profile-products',
  )
  .add(
    'company-profile-product',
    `${maybePrefix}/companies/:slug/products/:id`,
    'company-profile-product',
  )
  .add(
    'company-profile-fashion-finder',
    `${maybePrefix}/companies/:slug/fashion-finder`,
    'company-profile-fashion-finder',
  )
  .add(
    'fashion-finder',
    `${maybePrefix}/fashion-finder`,
    'fashion-finder',
  )
  .add(
    'fashion-finder-profile',
    `${maybePrefix}/find/:slug/products`,
    'fashion-finder-profile',
  )
  .add(
    'fashion-finder-product',
    `${maybePrefix}/find/:slug/products/:id`,
    'fashion-finder-product',
  )
  .add(
    'school-profile-courses',
    `${maybePrefix}/companies/:slug/courses`,
    'school-profile-courses',
  )
  .add(
    'courses',
    `${maybePrefix}/education/courses`,
    'courses',
  )
  .add(
    'course',
    String.raw`${maybePrefix}/education/courses/:id(\d+)-:slug([\w-]+)`,
    'course',
  )
  .add(
    'product',
    String.raw`${maybePrefix
    }{/markets/:marketId(\d+)}?{/stands/:standId(\d+)}?/products/:id`,
    'product',
  )
  .add(
    'market-brands',
    String.raw`${maybePrefix}/markets/:marketId(\d+)/brands`,
    'market-brands',
  )
  .add(
    'brands-directory',
    `${maybePrefix}/brands{/:section(all)}?`,
    'brands-directory',
  )
  .add(
    'schools-directory',
    `${maybePrefix}/schools{/:section(all)}?`,
    'schools-directory',
  )
  .add(
    'company-directory',
    `${maybePrefix}/companies`,
    'company-directory',
  )
  .add(
    'federations-directory',
    `${maybePrefix}/federations{/:section(all)}?`,
    'federations-directory',
  )
  .add(
    'manufacturer-directory',
    `${maybePrefix}/manufacturers{/:section(all)}?`,
    'manufacturer-directory',
  )
  .add(
    'software-suppliers-directory',
    `${maybePrefix}/software-in-fashion{/:section(all)}?`,
    'software-suppliers-directory',
  )
  .add(
    'fashionweeks-directory',
    `${maybePrefix}/fashion-weeks{/:section(all)}?`,
    'fashionweeks-directory',
  )
  .add(
    'agency-directory',
    `${maybePrefix}/agencies{/:section(all)}?`,
    'agency-directory',
  )
  .add(
    'retail-directory',
    `${maybePrefix}/retailers{/:section(all)}?`,
    'retail-directory',
  )
  .add(
    'specials',
    `${maybePrefix}/specials/:slug`,
    'specials',
  )
  .add(
    'specials-directory',
    `${maybePrefix}/specials`,
    'specials-directory',
  )
  .add(
    'events',
    `${maybePrefix}/:eventsPath(events|eventos|veranstaltungen|\
evenementen|evenements|eventi|kalendar)`,
    'events',
  )
  .add(
    'event',
    `${maybePrefix}/:eventsPath(events|eventos|veranstaltungen|\
evenementen|evenements|eventi|kalendar)/:slug`,
    'event',
  )
  .add(
    'products',
    String.raw`${maybePrefix
    }{/markets/:marketId(\d+)}?{/stands/:standId(\d+)}?/products`,
    'products',
  )
  .add(
    'markets',
    `${maybePrefix}/markets`,
    'markets',
  )
  .add(
    'market',
    String.raw`${maybePrefix}/markets/:marketId(\d+)`,
    'market',
  )
  .add(
    'stands',
    String.raw`${maybePrefix}/markets/:marketId(\d+)/stands`,
    'stands',
  )
  .add(
    'stand',
    String.raw`${maybePrefix}/markets/:marketId(\d+)/stands/:standId(\d+)`,
    'stand',
  )
  .addPathForLocales(
    'jobs-positions',
    englishJobsPositionsPath,
    [
      ...untranslatedLocales,
      ...portugueseLocales,
      ...englishLocales,
    ],
  )
  .addPathForLocales(
    'jobs-positions',
    germanJobsPositionsPath,
    germanLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    spanishJobsPositionsPath,
    spanishLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    frenchJobsPositionsMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    frenchJobsPositionsPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    hungarianJobsPositionsPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    italianJobsPositionsPath,
    italianLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    flemishJobsPositionsPath,
    flemishLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    dutchJobsPositionsPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'jobs-positions',
    russianJobsPositionsPath,
    russianLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    englishJobsCitiesPath,
    [
      ...untranslatedLocales,
      ...portugueseLocales,
      ...englishLocales,
    ],
  )
  .addPathForLocales(
    'jobs-cities',
    germanJobsCitiesPath,
    germanLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    spanishJobsCitiesPath,
    spanishLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    frenchJobsCitiesMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    frenchJobsCitiesPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    hungarianJobsCitiesPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    italianJobsCitiesPath,
    italianLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    flemishJobsCitiesPath,
    flemishLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    dutchJobsCitiesPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'jobs-cities',
    russianJobsCitiesPath,
    russianLocales,
  )
  .addPathForLocales(
    'company-profiles',
    englishCompanyProfilesPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'company-profiles',
    germanCompanyProfilesPath,
    germanLocales,
  )
  .addPathForLocales(
    'company-profiles',
    spanishCompanyProfilesPath,
    spanishLocales,
  )
  .addPathForLocales(
    'company-profiles',
    frenchCompanyProfilesMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'company-profiles',
    frenchCompanyProfilesPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'company-profiles',
    hungarianCompanyProfilesPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'company-profiles',
    italianCompanyProfilesPath,
    italianLocales,
  )
  .addPathForLocales(
    'company-profiles',
    flemishCompanyProfilesPath,
    flemishLocales,
  )
  .addPathForLocales(
    'company-profiles',
    dutchCompanyProfilesPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'company-profiles',
    russianCompanyProfilesPath,
    russianLocales,
  )
  .addPathForLocales(
    'careers-ebp',
    englishCareersPagePath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    germanCareersPagePath,
    germanLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    spanishCareersPagePath,
    spanishLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    frenchCareersPageMultilingualPath,
    frenchMultilingualLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    frenchCareersPagePath,
    frenchNonMultilingualLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    hungarianCareersPagePath,
    hungarianLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    italianCareersPagePath,
    italianLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    flemishCareersPagePath,
    flemishLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    dutchCareersPagePath,
    netherlandsLocales,
    'careers',
  )
  .addPathForLocales(
    'careers-ebp',
    russianCareersPagePath,
    russianLocales,
    'careers',
  )
  .addPathForLocales(
    'careers',
    englishJobsCareersPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'careers',
    germanJobsCareersPath,
    germanLocales,
  )
  .addPathForLocales(
    'careers',
    spanishJobsCareersPath,
    spanishLocales,
  )
  .addPathForLocales(
    'careers',
    frenchJobsCareersMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'careers',
    frenchJobsCareersPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'careers',
    hungarianJobsCareersPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'careers',
    italianJobsCareersPath,
    italianLocales,
  )
  .addPathForLocales(
    'careers',
    flemishJobsCareersPath,
    flemishLocales,
  )
  .addPathForLocales(
    'careers',
    dutchJobsCareersPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'careers',
    russianJobsCareersPath,
    russianLocales,
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    englishJobsCareersCityPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    germanJobsCareersCityPath,
    germanLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    spanishJobsCareersCityPath,
    spanishLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    frenchJobsCareersMultilingualCityPath,
    frenchMultilingualLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    frenchJobsCareersCityPath,
    frenchNonMultilingualLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    hungarianJobsCareersCityPath,
    hungarianLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    italianJobsCareersCityPath,
    italianLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    flemishJobsCareersCityPath,
    flemishLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    dutchJobsCareersCityPath,
    netherlandsLocales,
    'careers',
  )
  .addPathForLocales(
    'careers.onlyLocationSearch',
    russianJobsCareersCityPath,
    russianLocales,
    'careers',
  )
  .addPathForLocales(
    'company-ebp',
    englishProfilePagePath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    germanProfilePagePath,
    germanLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    spanishProfilePagePath,
    spanishLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    frenchProfilePageMultilingualPath,
    frenchMultilingualLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    frenchProfilePagePath,
    frenchNonMultilingualLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    hungarianProfilePagePath,
    hungarianLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    italianProfilePagePath,
    italianLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    flemishProfilePagePath,
    flemishLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    dutchProfilePagePath,
    netherlandsLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp',
    russianProfilePagePath,
    russianLocales,
    'jobs',
  )
  .addPathForLocales(
    'company-ebp-edit',
    englishEBPEditPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'company-ebp-edit',
    germanEBPEditPath,
    germanLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    spanishEBPEditPath,
    spanishLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    frenchEBPEditMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    frenchEBPEditPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    hungarianEBPEditPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    italianEBPEditPath,
    italianLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    flemishEBPEditPath,
    flemishLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    dutchEBPEditPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'company-ebp-edit',
    russianEBPEditPath,
    russianLocales,
  )
  .addPathForLocales(
    'jobs',
    englishJobsPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'jobs',
    germanJobsPath,
    germanLocales,
  )
  .addPathForLocales(
    'jobs',
    spanishJobsPath,
    spanishLocales,
  )
  .addPathForLocales(
    'jobs',
    frenchJobsMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'jobs',
    frenchJobsPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'jobs',
    hungarianJobsPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'jobs',
    italianJobsPath,
    italianLocales,
  )
  .addPathForLocales(
    'jobs',
    flemishJobsPath,
    flemishLocales,
  )
  .addPathForLocales(
    'jobs',
    dutchJobsPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'jobs',
    russianJobsPath,
    russianLocales,
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    englishJobsCityPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    germanJobsCityPath,
    germanLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    spanishJobsCityPath,
    spanishLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    frenchJobsMultilingualCityPath,
    frenchMultilingualLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    frenchJobsCityPath,
    frenchNonMultilingualLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    hungarianJobsCityPath,
    hungarianLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    italianJobsCityPath,
    italianLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    flemishJobsCityPath,
    flemishLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    dutchJobsCityPath,
    netherlandsLocales,
    'jobs',
  )
  .addPathForLocales(
    'jobs.onlyLocationSearch',
    russianJobsCityPath,
    russianLocales,
    'jobs',
  )
  .addPathForLocales(
    'job-apply',
    englishJobApplyPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'job-apply',
    germanJobApplyPath,
    germanLocales,
  )
  .addPathForLocales(
    'job-apply',
    spanishJobApplyPath,
    spanishLocales,
  )
  .addPathForLocales(
    'job-apply',
    frenchJobApplyMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'job-apply',
    frenchJobApplyPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'job-apply',
    hungarianJobApplyPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'job-apply',
    italianJobApplyPath,
    italianLocales,
  )
  .addPathForLocales(
    'job-apply',
    flemishJobApplyPath,
    flemishLocales,
  )
  .addPathForLocales(
    'job-apply',
    dutchJobApplyPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'job-apply',
    russianJobApplyPath,
    russianLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    englishCareersJobApplyPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...portugueseLocales,
    ],
  )
  .addPathForLocales(
    'careers-job-apply',
    germanCareersJobApplyPath,
    germanLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    spanishCareersJobApplyPath,
    spanishLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    frenchCareersJobApplyMultilingualPath,
    frenchMultilingualLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    hungarianCareersJobApplyPath,
    hungarianLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    italianCareersJobApplyPath,
    italianLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    dutchCareersJobApplyPath,
    netherlandsLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    russianCareersJobApplyPath,
    russianLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    frenchCareersJobApplyPath,
    frenchNonMultilingualLocales,
  )
  .addPathForLocales(
    'careers-job-apply',
    flemishCareersJobApplyPath,
    flemishLocales,
  )
  .addPathForLocales(
    'editorial-statute',
    englishEditorialStatutePath,
    [
      ...englishLocales,
      ...hungarianLocales,
      ...portugueseLocales,
      ...untranslatedLocales,
    ],
  )
  .addPathForLocales(
    'editorial-statute',
    germanEditorialStatutePath,
    germanLocales,
  )
  .addPathForLocales(
    'editorial-statute',
    frenchEditorialStatutePath,
    [
      ...frenchNonMultilingualLocales,
      ...frenchMultilingualLocales,
    ],
  )
  .addPathForLocales(
    'editorial-statute',
    italianEditorialStatutePath,
    italianLocales,
  )
  .addPathForLocales(
    'editorial-statute',
    spanishEditorialStatutePath,
    spanishLocales,
  )
  .addPathForLocales(
    'editorial-statute',
    dutchEditorialStatutePath,
    dutchLocales,
  )
  .addPathForLocales(
    'editorial-statute',
    russianEditorialStatutePath,
    russianLocales,
  )
  .addPathForLocales(
    'submit-job-postings',
    englishSubmitJobPostingsPath,
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...italianLocales,
      ...portugueseLocales,
      ...hungarianLocales,
    ],
  )
  .addPathForLocales(
    'submit-job-postings',
    '/company/mode-vacature-plaatsen',
    ['nl-NL'],
  )
  .addPathForLocales(
    'submit-job-postings',
    '/company/plaats-uw-vacatures',
    ['nl-BE'],
  )
  .addPathForLocales(
    'submit-job-postings',
    germanSubmitJobPostingsPath,
    germanLocales,
  )
  .addPathForLocales(
    'submit-job-postings',
    frenchSubmitJobPostingsPath,
    [
      ...frenchMultilingualLocales,
      ...frenchNonMultilingualLocales,
    ],
  )
  .addPathForLocales(
    'submit-job-postings',
    spanishSubmitJobPostingsPath,
    spanishLocales,
  )
  .addPathForLocales(
    'submit-job-postings',
    '/company/opublikovat-vakansii',
    russianLocales,
  )
  .add(
    'category',
    `${maybePrefix}/${categoryPath}`,
    'category',
  )
  .add(
    'category-tag',
    `${maybePrefix}/${categoryTagPage}/:categoryTagPath`,
    'category-tag',
  )
  .add(
    'category-tag.categories',
    `${maybePrefix}/${categoryTagPage}/categories/:categoryTagPath`,
    'category-tag',
  )
  .add(
    'newsletter',
    `${maybePrefix}/newsletter{/:path(.+)}?`,
    'newsletter',
  )
  .add(
    'lite-home-page',
    maybePrefix,
    'lite-home-page',
  )
  .add(
    'about-us',
    `${maybePrefix}/about-us`,
    'about-us',
  )
  .add(
    'privacy',
    `${maybePrefix}/privacy`,
    'privacy',
  )
  .add(
    'code-of-ethics',
    `${maybePrefix}/code-of-ethics`,
    'code-of-ethics',
  )
  .add(
    'contact',
    `${maybePrefix}/contact`,
    'contact',
  )
  .add(
    'companydirectory',
    `${maybePrefix}/companydirectory`,
    'companydirectory',
  )
  .add(
    'conditions',
    `${maybePrefix}/conditions`,
    'conditions',
  )
  .add(
    'cookies',
    `${maybePrefix}/cookies`,
    'cookies',
  )
  .add(
    'fashion-education-network',
    `${maybePrefix}/fashion-education-network`,
    'fashion-education-network',
  )
  .add(
    'fashion-united-media',
    `${maybePrefix}/fashion-united-media`,
    'fashion-united-media',
  )
  .add(
    'fashionunited-career-center',
    `${maybePrefix}/fashionunited-career-center`,
    'fashionunited-career-center',
  )
  .add(
    'fashionunited-news',
    `${maybePrefix}/fashionunited-news`,
    'fashionunited-news',
  )
  .add(
    'fashionunited-newsletter',
    `${maybePrefix}/fashionunited-newsletter`,
    'fashionunited-newsletter',
  )
  .add(
    'fashionweekweb',
    `${maybePrefix}/fashionweekweb`,
    'fashionweekweb',
  )
  .add(
    'international-network',
    `${maybePrefix}/international-network`,
    'international-network',
  )
  .add(
    'terms-of-use',
    `${maybePrefix}/terms-of-use`,
    'terms-of-use',
  )
  .add(
    'privacy-statement',
    `${maybePrefix}/privacy-statement`,
    'privacy-statement',
  )
  .add(
    'employer-branding',
    '/company/employer-branding',
    'employer-branding',
  )
  .add(
    'content-marketing',
    '/company/content-marketing',
    'content-marketing',
  )
  .add(
    'display-advertising',
    '/company/advertise',
    'display-advertising',
  )
  .add(
    'intelligence',
    '/i',
    'intelligence',
  )
  .add(
    'company',
    '/company',
    'company',
  )
  .add(
    'facebook-fashion-index',
    '/i/facebook-fashion-index',
    'facebook-fashion-index',
  )
  .add(
    'most-valuable-fashion-brands',
    '/i/most-valuable-fashion-brands',
    'most-valuable-fashion-brands',
  )
  .add(
    'richest-people-in-fashion',
    '/i/richest-people-in-fashion',
    'richest-people-in-fashion',
  )
  .add(
    'twitter-fashion-index',
    '/i/twitter-fashion-index',
    'twitter-fashion-index',
  )
  .add(
    'top100',
    '/i/top100',
    'top100',
  )
  .add(
    'top200',
    '/i/top200',
    'top200',
  )
  .add(
    'sustainability-certification-organizations-in-fashion',
    '/i/sustainability-certification-organizations-in-fashion',
    'sustainability-certification-organizations-in-fashion',
  )
  .add(
    'tiktok-fashion-index',
    '/i/tiktok-fashion-index',
    'tiktok-fashion-index',
  )
  .add(
    'instagram-fashion-index',
    '/i/instagram-fashion-index',
    'instagram-fashion-index',
  )
  .add(
    'global-fashion-industry-statistics',
    '/statistics/global-fashion-industry-statistics{/:region}?',
    'global-fashion-industry-statistics',
  )
  .add(
    'statistics',
    '/statistics',
    'statistics',
  )
  .add(
    'statistic',
    '/statistics{/:slug(china|czech-republic|denmark|finland|hungary|japan|norway|poland|portugal|russia|sweden|turkey|european-union)}?',
    'statistic',
  )
  .add(
    'marketplace',
    '/marketplace',
    'marketplace',
  )
  .add(
    'marketplace-about',
    '/marketplace/about',
    'marketplace-about',
  )
  .add(
    'marketplace-brands',
    '/marketplace/brands',
    'marketplace-brands',
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/statistieken-modebranche-nederland',
    ['nl-NL'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/uk-fashion-industry-statistics',
    ['en-GB'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/zahlen-und-fakten-zur-deutschen-modeindustrie',
    ['de-DE'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/estadisticas-de-consumo-y-ventas-de-la-moda-en-espana',
    ['es-ES'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/industrie-de-la-mode-habillement-statistiques-france',
    ['fr-FR'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/fashion-industry-statistics-india',
    ['en-IN'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/statistiche-moda-italia',
    ['it-IT'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/estadisticas-de-la-industria-de-la-moda-en-peru',
    ['es-PE'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/estadisticas-de-la-industria-de-la-moda-en-colombia',
    ['es-CO'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/estadisticas-de-la-industria-de-la-moda-en-argentina',
    ['es-AR'],
  )
  .addPathForLocales(
    'country-statistics',
    '/statistics/estadisticas-de-la-industria-de-la-moda-en-chile',
    ['es-CL'],
  )
  .add(
    'international-fashion-jobs',
    '/landing/international-fashion-jobs',
    'international-fashion-jobs',
  )
  .addPathForLocales(
    'fashion-internships',
    '/landing/fashion-internships-in/global',
    ['en-US'],
  )
  .add(
    'fashionweeks-around-the-world-list',
    '/landing/fashionweeks-around-the-world-list',
    'fashionweeks-around-the-world-list',
  )
  .addPathForLocales(
    'positions-in-fashion',
    '/jobs/work/positions-in-fashion',
    [
      ...englishLocales,
      ...untranslatedLocales,
      ...hungarianLocales,
      ...russianLocales,
    ],
  )
  .addPathForLocales(
    'positions-in-fashion',
    '/jobs/work/berufe-in-der-modebranche',
    ['de-DE'],
  )
  .addPathForLocales(
    'positions-in-fashion',
    '/jobs/work/functieprofielen-in-de-modebranche',
    ['nl-NL'],
  )
  .addPathForLocales(
    'positions-in-fashion',
    '/jobs/work/informacion-sobre-el-trabajo-en-la-moda',
    ['es-ES'],
  )
  .add(
    'subscribe',
    String.raw`/subscribe{/:referralCode(\w+)}?`,
    'subscribe',
  );

const routes = (typeof window !== 'undefined'
  && isDashboard(window.location.href))
  ? getDashboardRoutes() : getSiteRoutes();

const { Router: RoutesRouter } = routes;

export const Router = RoutesRouter;

const addPrefix = (intl, parameters) => {
  const prefix = intl.formatMessage(messages.prefix);

  if (prefix !== 'fr') {
    return parameters;
  }

  return {
    ...parameters,
    prefix,
  };
};

const addEventsPath = (intl, route, parameters) => {
  if (route !== 'events' && route !== 'event') {
    return parameters;
  }

  const eventsPath = intl.formatMessage(messages.eventsPath);

  return {
    ...parameters,
    eventsPath,
  };
};

const addSearchPath = (intl, route, parameters) => {
  if (route !== 'search') {
    return parameters;
  }

  const searchPath = intl.formatMessage(messages.searchPath);

  return {
    ...parameters,
    searchPath,
  };
};

const updateEventsHref = (intl, route, href) => {
  if (route !== 'events' && route !== 'event') {
    return href;
  }

  return `/${intl.formatMessage(messages.eventsPath)}`;
};

export const Link = React.forwardRef((properties, reference) => {
  const intl = useIntl();
  const {
    route,
    params,
    to,
    children,
    passHref,
    scroll,
    onClick,
    href: originalHref,
    as: originalAs,
  } = properties;
  const nameOrUrl = route || to;
  const newParameters = addSearchPath(
    intl,
    route,
    addEventsPath(intl, route, addPrefix(intl, params)),
  );
  const { as, href } = nameOrUrl
    ? routes.findAndGetUrls(nameOrUrl, newParameters).urls
    : { as: originalAs, href: updateEventsHref(intl, route, originalHref) };

  return React.createElement(NextLink, {
    passHref,
    as,
    href,
    ref: reference,
    scroll,
    onClick,
    prefetch: false,
  }, children);
});

Link.displayName = 'Link';

Link.propTypes = {
  params: PropTypes.shape({}),
};

Link.defaultProps = {
  params: {},
};

Link.displayName = 'Link';

['pushRoute', 'replaceRoute'].forEach((routerMethodName) => {
  const routerMethod = Router[routerMethodName].bind(Router);

  // eslint-disable-next-line default-param-last
  Router[routerMethodName] = (route, originalParameters = {}, options) => {
    const parameters = getRelativePrefix(window.location.href) === '/fr' ? {
      ...originalParameters,
      prefix: 'fr',
    } : originalParameters;

    routerMethod(route, parameters, options);
  };
});

export default routes;
